/* VideoLinks EMI widget — live Razorpay affordability data, minimal UI.
   Data: api.razorpay.com/v1/checkout/affordability (same feed as Razorpay's own widget), via JSONP.
   Fallback: assets/vl-emi-fallback.json snapshot, only used if the live call fails. */
(function () {
  'use strict';
  if (window.__vlEmiLoaded) return;
  window.__vlEmiLoaded = true;

  var API = 'https://api.razorpay.com/v1/checkout/affordability';
  var BANK_LOGO = 'https://cdn.razorpay.com/bank/';
  var CARDLESS_LOGO = 'https://cdn.razorpay.com/cardless_emi-sq/';
  var PAYLATER_LOGO = 'https://cdn.razorpay.com/paylater-sq/';
  var LOGO_ALIAS = { HDFC_DC: 'HDFC', ICIC_DC: 'ICIC', INDB_DC: 'INDB', KKBK_DC: 'KKBK', UTIB_DC: 'UTIB' };
  var SHORT_NAMES = {
    HDFC: 'HDFC Bank', HDFC_DC: 'HDFC Bank Debit Card', ICIC: 'ICICI Bank', UTIB: 'Axis Bank', SBIN: 'SBI',
    KKBK: 'Kotak Bank', AMEX: 'American Express', BARB: 'Bank of Baroda', FDRL: 'Federal Bank', HSBC: 'HSBC',
    IDFB: 'IDFC FIRST Bank', INDB: 'IndusInd Bank', RATN: 'RBL Bank', SCBL: 'Standard Chartered', YESB: 'Yes Bank',
    AUBL: 'AU Small Finance Bank', onecard: 'OneCard'
  };
  var MAX_FALLBACK_AGE_DAYS = 45;
  var jsonpSeq = 0;

  /* ---------- helpers ---------- */
  function inr(paise, decimals) {
    var v = paise / 100;
    return '₹' + v.toLocaleString('en-IN', { maximumFractionDigits: decimals || 0, minimumFractionDigits: decimals || 0 });
  }
  // Standard reducing-balance EMI. principal in paise, annual rate in %, n months.
  function emi(principal, rate, n) {
    if (!rate) return principal / n;
    var r = rate / 1200;
    var f = Math.pow(1 + r, n);
    return principal * r * f / (f - 1);
  }
  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function storageGet(k) { try { return JSON.parse(sessionStorage.getItem(k)); } catch (e) { return null; } }
  function storageSet(k, v) { try { sessionStorage.setItem(k, JSON.stringify(v)); } catch (e) {} }

  function jsonp(url, timeoutMs) {
    return new Promise(function (resolve, reject) {
      // Razorpay only accepts plain/namespaced callback names (no leading underscore).
      var ns = window.VLEmiJsonp = window.VLEmiJsonp || {};
      var id = 'cb' + (jsonpSeq++);
      var name = 'VLEmiJsonp.' + id;
      var s = document.createElement('script');
      var done = false;
      var timer = setTimeout(function () { finish(new Error('timeout')); }, timeoutMs);
      function finish(err, data) {
        if (done) return;
        done = true;
        clearTimeout(timer);
        ns[id] = function () {};
        if (s.parentNode) s.parentNode.removeChild(s);
        err ? reject(err) : resolve(data);
      }
      ns[id] = function (data) { finish(null, data); };
      s.onerror = function () { finish(new Error('network')); };
      s.async = true;
      s.src = url + (url.indexOf('?') > -1 ? '&' : '?') + 'callback=' + name;
      document.head.appendChild(s);
    });
  }

  function loadData(cfg) {
    var cacheKey = 'vl-emi:' + cfg.key;
    var cached = storageGet(cacheKey);
    if (cached && Date.now() - cached.t < 30 * 60 * 1000) return Promise.resolve({ data: cached.d, source: 'live' });
    var url = API + '?key_id=' + encodeURIComponent(cfg.key) + '&amount=' + cfg.amount;
    return jsonp(url, 6000).then(function (d) {
      if (!d || !d.entities) throw new Error('bad payload');
      storageSet(cacheKey, { t: Date.now(), d: d });
      return { data: d, source: 'live' };
    }).catch(function () {
      if (!cfg.fallbackUrl) return null;
      return fetch(cfg.fallbackUrl, { credentials: 'omit' }).then(function (r) { return r.json(); }).then(function (snap) {
        var age = (Date.now() - new Date(snap.captured_at).getTime()) / 864e5;
        if (!(age <= MAX_FALLBACK_AGE_DAYS)) return null; // too old: hide rather than show stale rates
        return { data: snap.data, source: 'fallback' };
      }).catch(function () { return null; });
    });
  }

  /* ---------- plan building ---------- */
  function buildPlans(data, amount, hidden) {
    var ent = data.entities || {};
    var names = (data.mappings && data.mappings.banks) || {};
    var otherNames = (data.mappings && data.mappings.other_emi) || {};
    var out = { card: [], cardless: [], paylater: [], minMonthly: Infinity, minTenure: 0 };

    function addIssuer(code, values, nameMap) {
      if (hidden.indexOf(code.toLowerCase()) > -1) return;
      var plans = (values || []).filter(function (p) { return amount >= (p.min_amount || 0); })
        .map(function (p) {
          var noCost = p.subvention === 'merchant';
          var rate = noCost ? 0 : Number(p.interest) || 0;
          var m = emi(amount, rate, p.duration);
          return { n: p.duration, rate: rate, monthly: m, total: m * p.duration, interest: m * p.duration - amount, noCost: noCost };
        })
        .sort(function (a, b) { return a.n - b.n; });
      // de-duplicate durations (keep lowest rate)
      var seen = {};
      plans = plans.filter(function (p) {
        if (seen[p.n] && seen[p.n].rate <= p.rate) return false;
        seen[p.n] = p; return true;
      });
      if (!plans.length) return;
      var best = plans.reduce(function (a, b) { return b.monthly < a.monthly ? b : a; });
      if (best.monthly < out.minMonthly) { out.minMonthly = best.monthly; out.minTenure = best.n; }
      var debit = /_DC$/.test(code);
      out.card.push({
        code: code,
        name: SHORT_NAMES[code] || nameMap[code] || names[code] || code,
        logo: BANK_LOGO + (LOGO_ALIAS[code] || code) + '.gif',
        debit: debit,
        from: best.monthly,
        minRate: Math.min.apply(null, plans.map(function (p) { return p.rate; })),
        noCost: plans.some(function (p) { return p.noCost; }),
        plans: plans
      });
    }

    var emiItems = (ent.emi && ent.emi.items) || {};
    var order = (ent.emi && ent.emi.order) || Object.keys(emiItems);
    order.forEach(function (c) { if (emiItems[c]) addIssuer(c, emiItems[c].values, names); });
    var other = (ent.other_emi && ent.other_emi.items) || {};
    Object.keys(other).forEach(function (c) { addIssuer(c, other[c].values, otherNames); });

    var cl = (ent.cardless_emi && ent.cardless_emi.providers) || {};
    var clNames = (data.mappings && data.mappings.cardless_emi) || {};
    Object.keys(cl).forEach(function (c) {
      var p = cl[c];
      if (!p.enabled || hidden.indexOf(c.toLowerCase()) > -1 || amount < (p.min_amount || 0)) return;
      out.cardless.push({ code: c, name: clNames[c] || c, logo: CARDLESS_LOGO + c + '.svg' });
    });
    var pl = (ent.paylater && ent.paylater.providers) || {};
    var plNames = (data.mappings && data.mappings.paylater) || {};
    Object.keys(pl).forEach(function (c) {
      var p = pl[c];
      if (!p.enabled || hidden.indexOf(c.toLowerCase()) > -1 || amount < (p.min_amount || 0)) return;
      out.paylater.push({ code: c, name: plNames[c] || c, logo: PAYLATER_LOGO + c + '.svg' });
    });
    return out;
  }

  /* ---------- rendering ---------- */
  var ICON_CARD = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="2.5" y="5" width="19" height="14" rx="2.5"/><path d="M2.5 9.5h19M6.5 15h3"/></svg>';
  var ICON_CHEV = '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 6 6 6-6 6"/></svg>';
  var ICON_X = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6 6 18"/></svg>';

  function renderBar(root, plans) {
    var count = plans.card.length;
    var extras = [];
    if (plans.cardless.length) extras.push('Cardless');
    if (plans.paylater.length) extras.push('Pay Later');
    var logos = plans.card.filter(function (b) { return !b.debit; }).slice(0, 3).map(function (b) {
      return '<img src="' + b.logo + '" alt="" width="20" height="20" loading="lazy" decoding="async">';
    }).join('');
    root.innerHTML =
      '<button type="button" class="vl-emi__bar" aria-haspopup="dialog">' +
        '<span class="vl-emi__icon">' + ICON_CARD + '</span>' +
        '<span class="vl-emi__text">' +
          '<span class="vl-emi__main">Pay <strong>' + inr(plans.minMonthly) + '</strong>/month with EMI</span>' +
          '<span class="vl-emi__sub"><span class="vl-emi__logos" aria-hidden="true">' + logos + '</span>' +
            count + ' bank' + (count === 1 ? '' : 's') + (extras.length ? ' \u00B7 ' + extras.join(' & ') : '') + '</span>' +
        '</span>' +
        '<span class="vl-emi__cta"><span class="vl-emi__ctatext">View plans</span>' + ICON_CHEV + '</span>' +
      '</button>';
    root.classList.add('is-ready');
  }

  function bankRow(b, amount, idx, bestMonthly) {
    var lowest = Math.min.apply(null, b.plans.map(function (p) { return p.monthly; }));
    var rows = b.plans.map(function (p) {
      var isLow = p.monthly === lowest;
      return '<tr' + (isLow ? ' class="is-low"' : '') + '><td>' + p.n + ' mo' + (isLow ? '<span class="vl-emi__tag">Lowest</span>' : '') + '</td>' +
        '<td><strong>' + inr(p.monthly) + '</strong></td>' +
        '<td>' + (p.noCost ? '<span class="vl-emi__nocost">No cost</span>' : p.rate + '%') + '</td>' +
        '<td>' + inr(p.total) + '</td></tr>';
    }).join('');
    var best = Math.round(b.from) === Math.round(bestMonthly);
    return '<div class="vl-emi__bank">' +
      '<button type="button" class="vl-emi__bankhead" aria-expanded="false" aria-controls="vl-emi-b' + idx + '">' +
        '<span class="vl-emi__banklogo"><img src="' + b.logo + '" alt="" width="24" height="24" loading="lazy" decoding="async"></span>' +
        '<span class="vl-emi__bankname">' + esc(b.name) + (best ? '<span class="vl-emi__best">Lowest EMI</span>' : '') + '</span>' +
        '<span class="vl-emi__bankfrom"><small>from</small> <strong>' + inr(b.from) + '</strong>/mo</span>' +
        '<span class="vl-emi__chev">' + ICON_CHEV + '</span>' +
      '</button>' +
      '<div class="vl-emi__plans" id="vl-emi-b' + idx + '" hidden>' +
        '<table><thead><tr><th>Tenure</th><th>Monthly</th><th>Interest</th><th>Total</th></tr></thead><tbody>' + rows + '</tbody></table>' +
      '</div></div>';
  }

  function providerList(items, note) {
    return '<p class="vl-emi__note">' + note + '</p><ul class="vl-emi__providers">' + items.map(function (p) {
      return '<li><img src="' + p.logo + '" alt="" width="28" height="28" loading="lazy" decoding="async"><span>' + esc(p.name) + '</span></li>';
    }).join('') + '</ul>';
  }

  function openSheet(root, plans, amount) {
    var credit = plans.card.filter(function (b) { return !b.debit; });
    var debit = plans.card.filter(function (b) { return b.debit; });
    var tabs = [];
    if (credit.length) tabs.push({ id: 'credit', label: 'Credit Card', html: credit.map(function (b, i) { return bankRow(b, amount, 'c' + i, plans.minMonthly); }).join('') });
    if (debit.length) tabs.push({ id: 'debit', label: 'Debit Card', html: debit.map(function (b, i) { return bankRow(b, amount, 'd' + i, plans.minMonthly); }).join('') });
    if (plans.cardless.length) tabs.push({ id: 'cardless', label: 'Cardless', html: providerList(plans.cardless, 'Pay in instalments without a card. Plans and eligibility are shown at checkout.') });
    if (plans.paylater.length) tabs.push({ id: 'paylater', label: 'Pay Later', html: providerList(plans.paylater, 'Buy now and pay later. Eligibility is checked at checkout.') });

    var overlay = document.createElement('div');
    overlay.className = 'vl-emi-overlay';
    overlay.innerHTML =
      '<div class="vl-emi-sheet" role="dialog" aria-modal="true" aria-labelledby="vl-emi-title">' +
        '<div class="vl-emi-sheet__grab" aria-hidden="true"></div>' +
        '<header class="vl-emi-sheet__head">' +
          '<span class="vl-emi-sheet__badge">' + ICON_CARD + '</span>' +
          '<div class="vl-emi-sheet__title"><h2 id="vl-emi-title">EMI options</h2><p>On ' + inr(amount) + ' \u00B7 from <strong>' + inr(plans.minMonthly) + '/month</strong></p></div>' +
          '<button type="button" class="vl-emi-sheet__close" aria-label="Close">' + ICON_X + '</button>' +
        '</header>' +
        '<div class="vl-emi-sheet__tabs" role="tablist">' + tabs.map(function (t, i) {
          return '<button type="button" role="tab" data-tab="' + t.id + '" aria-selected="' + (i === 0) + '">' + t.label + '</button>';
        }).join('') + '</div>' +
        '<div class="vl-emi-sheet__body">' + tabs.map(function (t, i) {
          return '<div role="tabpanel" data-panel="' + t.id + '"' + (i === 0 ? '' : ' hidden') + '>' + t.html + '</div>';
        }).join('') + '</div>' +
        '<footer class="vl-emi-sheet__foot">Interest is charged by your bank; GST applies on interest. Final EMI is confirmed at checkout. Powered by Razorpay.</footer>' +
      '</div>';

    var prevFocus = document.activeElement;
    var prevOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.appendChild(overlay);
    requestAnimationFrame(function () { overlay.classList.add('is-open'); });
    var closeBtn = overlay.querySelector('.vl-emi-sheet__close');
    closeBtn.focus();

    function close() {
      overlay.classList.remove('is-open');
      document.documentElement.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
      setTimeout(function () { overlay.remove(); if (prevFocus) prevFocus.focus(); }, 220);
    }
    function onKey(e) { if (e.key === 'Escape') close(); }
    document.addEventListener('keydown', onKey);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay || e.target.closest('.vl-emi-sheet__close')) return close();
      var tab = e.target.closest('[role="tab"]');
      if (tab) {
        overlay.querySelectorAll('[role="tab"]').forEach(function (t) { t.setAttribute('aria-selected', t === tab); });
        overlay.querySelectorAll('[role="tabpanel"]').forEach(function (p) { p.hidden = p.getAttribute('data-panel') !== tab.getAttribute('data-tab'); });
        return;
      }
      var head = e.target.closest('.vl-emi__bankhead');
      if (head) {
        var open = head.getAttribute('aria-expanded') === 'true';
        head.setAttribute('aria-expanded', !open);
        document.getElementById(head.getAttribute('aria-controls')).hidden = open;
      }
    });
  }

  /* ---------- init per widget ---------- */
  function currentAmount(root, cfg) {
    var form = document.querySelector('form[data-type="add-to-cart-form"][data-product-id="' + cfg.productId + '"]') ||
      document.querySelector('form[data-type="add-to-cart-form"]');
    var input = form && form.querySelector('[name="id"]');
    var id = (input && input.value) || new URLSearchParams(location.search).get('variant');
    var v = cfg.variants.filter(function (x) { return String(x.id) === String(id); })[0];
    return v ? v.price : cfg.amount;
  }

  function init(root) {
    var cfg;
    try { cfg = JSON.parse(root.querySelector('script[type="application/json"]').textContent); } catch (e) { return; }
    var hidden = (cfg.hide || []).map(function (h) { return String(h).toLowerCase(); });
    var state = { data: null, amount: cfg.amount, plans: null };

    function render() {
      if (!state.data) return;
      if (state.amount < (cfg.minPrice || 0)) { root.classList.add('is-empty'); root.innerHTML = ''; return; }
      state.plans = buildPlans(state.data, state.amount, hidden);
      if (!state.plans.card.length) { root.classList.add('is-empty'); root.innerHTML = ''; return; }
      root.classList.remove('is-empty');
      renderBar(root, state.plans);
    }

    root.addEventListener('click', function (e) {
      if (e.target.closest('.vl-emi__bar') && state.plans) openSheet(root, state.plans, state.amount);
    });

    var t;
    document.addEventListener('change', function () {
      clearTimeout(t);
      t = setTimeout(function () {
        var a = currentAmount(root, cfg);
        if (a !== state.amount) { state.amount = a; render(); }
      }, 250);
    });

    loadData(cfg).then(function (res) {
      if (!res) { root.classList.add('is-empty'); root.innerHTML = ''; return; }
      state.data = res.data;
      state.amount = currentAmount(root, cfg);
      root.setAttribute('data-source', res.source);
      render();
    });
  }

  function boot() { document.querySelectorAll('[data-vl-emi]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
